import type { Conversation } from '@twilio/conversations';

import { useCallback, useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react-native';

import { Toast } from '@shared/utils';
import { getConversationAttributes } from '@south-street-app/utils';
import {
  useCreateOrGetSupport,
  useGetCurrentEndUser,
  useSetPending,
} from '@utility-nyc/react-query-sdk';

import { ConversationChatView } from '../ChatScreen/ConversationChatView';

const SENTRY_TAG_TYPE = 'screen';
const SENTRY_TAG_CONTEXT = 'SupportConversationContent';

type SupportConversationContentProps = {
  conversation: Conversation | null;
  inlineTitle?: string;
};

const SupportConversationContent = ({
  conversation,
  inlineTitle,
}: SupportConversationContentProps) => {
  const { mutateAsync: setPending } = useSetPending();
  const { mutateAsync: createOrGetSupport, isPending: isProvisioningSupport } =
    useCreateOrGetSupport();
  const { data: currentUser } = useGetCurrentEndUser();
  const requestedSupportForUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const endUserId = currentUser?.data.id;

    if (!endUserId || conversation) {
      requestedSupportForUserIdRef.current = null;

      return;
    }

    if (requestedSupportForUserIdRef.current === endUserId) {
      return;
    }

    requestedSupportForUserIdRef.current = endUserId;

    createOrGetSupport({ endUserId }).catch((error) => {
      requestedSupportForUserIdRef.current = null;

      Sentry.captureException(error, {
        tags: {
          type: SENTRY_TAG_TYPE,
          context: SENTRY_TAG_CONTEXT,
          method: 'createOrGetSupport',
        },
        extra: {
          endUserId,
        },
      });

      Toast.showErrorToast({
        text1: 'Could not start chat',
        text2: 'Please try again.',
      });
    });
  }, [conversation, createOrGetSupport, currentUser?.data.id]);

  const onSendMessage = useCallback(
    async (message: string) => {
      if (!conversation) {
        return;
      }

      const attributes = getConversationAttributes(conversation);
      const conversationSid = conversation.uniqueName ?? conversation.sid;

      if (
        attributes.status === null ||
        typeof attributes.status === 'undefined' ||
        attributes.status === 'resolved'
      ) {
        try {
          await setPending({ sid: conversationSid });
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              type: SENTRY_TAG_TYPE,
              context: SENTRY_TAG_CONTEXT,
              method: 'setPending',
            },
            extra: {
              conversationSid,
              status: attributes.status ?? null,
            },
          });

          throw error;
        }

        Object.assign(conversation.attributes as Record<string, unknown>, {
          status: 'pending',
        });
      }

      try {
        await conversation.sendMessage(message);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            type: SENTRY_TAG_TYPE,
            context: SENTRY_TAG_CONTEXT,
            method: 'sendMessage',
          },
          extra: {
            conversationSid,
            status: attributes.status ?? null,
          },
        });

        throw error;
      }
    },
    [conversation, setPending],
  );

  return (
    <ConversationChatView
      conversation={conversation}
      emptyMessage={
        isProvisioningSupport
          ? 'Connecting you to Trading Desk chat...'
          : 'Trading Desk chat is not available yet.'
      }
      inlineTitle={inlineTitle}
      onSendMessage={onSendMessage}
      placeholder={'Message'}
    />
  );
};

export { SupportConversationContent };
